/**
 * @file Task-specific display and current-user task list helpers.
 */

import { state, appData } from '../../app/state/state.js';
import { getUserInitials } from './userDisplay.js';

/**
 * Gets a comma-separated string of unique initials for users associated with a task
 * (prioritizes assigned users, falls back to owner).
 * @param {object | null} task - The task object.
 * @returns {string} Comma-separated initials (e.g., "JD, AB"), empty string if no users found.
 */
export function getTaskInitials(task) {
    if (!task) {return '';}
    const userIds = getTaskDisplayUserIds(task);

    if (userIds.length === 0) {return '';}

    // Use Set to get unique initials, filter out "??" if desired, then join
    const initials = new Set(userIds.map(id => getUserInitials(id)).filter(init => init !== '??'));
    return Array.from(initials).join(', ');
}

/**
 * Resolves the user IDs displayed for a task card/table row.
 * @param {object | null} task Task record.
 * @returns {Array<string>} Assigned user IDs, or owner fallback.
 */
export function getTaskDisplayUserIds(task) {
    if (!task) {return [];}

    if (Array.isArray(task.assigned_users) && task.assigned_users.length > 0) {
        return task.assigned_users.filter(Boolean);
    }

    return task.owner ? [task.owner] : [];
}

/**
 * Returns the configured closed status used by task quick-close actions.
 * @returns {object | null} Closed task status config, or null if invalid.
 */
export function getTaskQuickCloseStatusConfig() {
    const quickCloseStatusId = appData?.config?.taskQuickCloseStatusId;
    if (!quickCloseStatusId) {return null;}

    const taskStatuses = appData?.config?.taskStatus || [];
    const quickCloseStatus = taskStatuses.find(
        (status) => status.id === quickCloseStatusId,
    );

    if (!quickCloseStatus || quickCloseStatus.isClosed !== true) {
        return null;
    }

    return quickCloseStatus;
}

/**
 * Filters the global `appData.tasks` list to return only tasks owned by or assigned to the current logged-in user.
 * @returns {Array<object>} An array of the current user's tasks. Returns empty array if no user or no tasks found.
 */
export function getUsersTasks() {
    const currentUserId = state?.currentUser?.uid;
    if (!currentUserId) {return [];} // No logged-in user

    const allTasks = appData?.tasks || []; // Safely access tasks
    if (allTasks.length === 0) {return [];} // No tasks loaded

    return allTasks.filter(t =>
        t.owner === currentUserId ||
        (Array.isArray(t.assigned_users) && t.assigned_users.includes(currentUserId))
    );
}
